import { useState } from 'react'
import { X, Building2, Loader2 } from 'lucide-react'
import { usePlans } from '../../hooks/use-plans'

interface CreateOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: {
    name: string
    billing_email: string
    billing_name?: string
    admin_email: string
    admin_name: string
    plan_id?: string
    skip_trial: boolean
    trial_days?: number
    send_invite: boolean
  }) => void
  isLoading?: boolean
}

export function CreateOrganizationModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: CreateOrganizationModalProps) {
  const [name, setName] = useState('')
  const [billingEmail, setBillingEmail] = useState('')
  const [billingName, setBillingName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminName, setAdminName] = useState('')
  const [skipTrial, setSkipTrial] = useState(false)
  const [trialDays, setTrialDays] = useState('14')
  const [sendInvite, setSendInvite] = useState(true)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const { data: plansResponse, isLoading: plansLoading } = usePlans()
  const paidPlans = (plansResponse?.data ?? []).filter((plan) => plan.slug !== 'trial')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      name,
      billing_email: billingEmail,
      billing_name: billingName || undefined,
      admin_email: adminEmail,
      admin_name: adminName,
      plan_id: skipTrial ? selectedPlanId : undefined,
      skip_trial: skipTrial,
      trial_days: skipTrial ? undefined : parseInt(trialDays),
      send_invite: sendInvite,
    })
  }

  const handleClose = () => {
    if (!isLoading) {
      setName('')
      setBillingEmail('')
      setBillingName('')
      setAdminEmail('')
      setAdminName('')
      setSkipTrial(false)
      setTrialDays('14')
      setSendInvite(true)
      setSelectedPlanId('')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-teal-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Nova Organização</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Organization Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
              Informações da Organização
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome da Organização *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ex: Clínica ABC"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de Cobrança *
                </label>
                <input
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  required
                  placeholder="financeiro@empresa.com"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome de Cobrança
                </label>
                <input
                  type="text"
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  placeholder="Razão social"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Admin User */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
              Administrador da Organização
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Admin *
                </label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  required
                  placeholder="Nome completo"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email do Admin *
                </label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  placeholder="admin@empresa.com"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-600">Enviar email de convite para o admin</span>
            </label>
          </div>

          {/* Trial Options */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
              Opções de Trial
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipTrial}
                onChange={(e) => {
                  setSkipTrial(e.target.checked)
                  if (!e.target.checked) {
                    setSelectedPlanId('')
                  }
                }}
                className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-600">Pular período de trial (ativar diretamente)</span>
            </label>
            {skipTrial && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plano Inicial *
                </label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  required={skipTrial}
                  disabled={plansLoading || paidPlans.length === 0}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">Selecione um plano</option>
                  {paidPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Organizações sem trial precisam nascer com uma subscription válida.
                </p>
              </div>
            )}
            {!skipTrial && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dias de Trial
                </label>
                <input
                  type="number"
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                  min="1"
                  max="90"
                  className="w-32 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Criar Organização
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
